import { useEffect, useMemo, useRef, useState } from 'react';

export interface SearchOption {
  value: string;
  label: string;
}

/**
 * A select with a search box.
 *
 * A native <select> is the right control until the list gets long enough that
 * finding an option means reading all of them — which is where the sub-status
 * lists are heading. This keeps the shape of a select (a closed button showing
 * the current value) and adds type-to-filter, so a list of thirty reads as
 * quickly as a list of three.
 *
 * Deliberately not a combobox that accepts free text: the value must be one of
 * the configured options, and letting someone type past the list only invites
 * the "why didn't my reason save" question.
 */
export function SearchSelect({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  emptyLabel = 'None',
  searchPlaceholder = 'Search…',
  disabled = false,
  id,
  ariaLabel,
}: {
  value: string | null;
  options: SearchOption[];
  onChange: (value: string | null) => void;
  placeholder?: string;
  /** Label for the "clear the value" row. Pass null to forbid clearing. */
  emptyLabel?: string | null;
  searchPlaceholder?: string;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // A stale filter on reopen looks like missing options.
  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setQuery('');
    setActive(0);
  }, [open]);

  const needle = query.trim().toLowerCase();
  const shown = useMemo(
    () => (needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options),
    [options, needle],
  );

  function choose(next: string | null) {
    onChange(next);
    setOpen(false);
    buttonRef.current?.focus();
  }

  return (
    <div className="search-select" ref={wrapRef}>
      <button
        ref={buttonRef}
        id={id}
        type="button"
        className="search-select-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={selected ? undefined : 'search-select-placeholder'}>
          {selected?.label ?? placeholder}
        </span>
        <span aria-hidden="true" className="search-select-caret">▾</span>
      </button>

      {open && (
        <div className="search-select-popover" role="listbox" aria-label={ariaLabel}>
          <div className="search-select-search">
            <input
              ref={searchRef}
              type="search"
              value={query}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setActive((i) => Math.min(i + 1, shown.length - 1));
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setActive((i) => Math.max(i - 1, 0));
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  const pick = shown[active];
                  if (pick) choose(pick.value);
                }
              }}
            />
          </div>

          <div className="search-select-list">
            {emptyLabel !== null && needle === '' && (
              <button
                type="button"
                className={`search-select-option${value === null ? ' is-selected' : ''}`}
                onClick={() => choose(null)}
              >
                <span className="search-select-none">{emptyLabel}</span>
              </button>
            )}

            {shown.map((option, i) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                className={
                  'search-select-option' +
                  (option.value === value ? ' is-selected' : '') +
                  (i === active ? ' is-active' : '')
                }
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(option.value)}
              >
                {option.label}
              </button>
            ))}

            {shown.length === 0 && (
              <p className="search-select-empty">
                {options.length === 0 ? 'Nothing to choose from.' : `No match for “${query.trim()}”.`}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
